import React, { useEffect, useMemo, useState } from "react";
import "./AdminUsersModule.css";
import {
  createAdminUser,
  listAdminRoles,
  listAdminUsers,
  setAdminUserStatus,
  updateAdminUser,
} from "./adminUsersApi";

const EMPTY_FORM = {
  id: null,
  name: "",
  email: "",
  password: "",
  roleIds: [],
};

const roleDescriptions = {
  "Newsletter Manager": "Manage subscribers, campaigns, previews, tests and newsletter history.",
  "Quiz Manager": "Create, edit, publish and review quizzes and quiz results.",
  "Registration Manager": "Manage student registration records and registration reports.",
  "Reports Viewer": "View operational reports and analytics without modification rights.",
};

function formatDate(value) {
  if (!value) return "Never";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "A";
}

function roleKey(role) {
  return String(role?.id ?? role?.role_id ?? "");
}

function Modal({ children, onClose, title, description }) {
  return (
    <div className="bb-au-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="bb-au-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bb-au-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="bb-au-modal-head">
          <div>
            <p className="bb-au-eyebrow">ADMINISTRATION</p>
            <h3 id="bb-au-modal-title">{title}</h3>
            {description ? <p>{description}</p> : null}
          </div>
          <button className="bb-au-icon-btn" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminUsersModule({ user, csrfToken }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  const canCreate = user?.permissions?.includes("admin_users.create");
  const canEdit = user?.permissions?.includes("admin_users.edit");
  const canDisable = user?.permissions?.includes("admin_users.disable");
  const canAssignRoles = user?.permissions?.includes("roles.assign");

  const selectableRoles = useMemo(
    () => roles.filter((role) => role.name !== "Super Admin"),
    [roles]
  );

  const activeCount = useMemo(
    () => users.filter((item) => item.status === "active").length,
    [users]
  );

  const protectedCount = useMemo(
    () => users.filter((item) => item.is_super_admin).length,
    [users]
  );

  function notify(message, type = "success") {
    setNotice({ message, type });
    window.setTimeout(() => setNotice(null), 3600);
  }

  async function loadUsers(page = 1) {
    setLoading(true);
    try {
      const data = await listAdminUsers({ page, limit: 20, search, status: statusFilter });
      setUsers(data.items || []);
      setPagination(data.pagination || { page, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      notify(error.message || "Unable to load administrator accounts.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function loadRoles() {
    setRolesLoading(true);
    try {
      const data = await listAdminRoles();
      setRoles(data.roles || []);
    } catch (error) {
      notify(error.message || "Unable to load roles.", "error");
    } finally {
      setRolesLoading(false);
    }
  }

  useEffect(() => {
    loadUsers(1);
  }, [statusFilter]);

  useEffect(() => {
    loadRoles();
  }, []);

  function openCreate() {
    setForm({ ...EMPTY_FORM, roleIds: [] });
    setShowForm(true);
  }

  function openEdit(item) {
    setForm({
      id: item.id,
      name: item.name || "",
      email: item.email || "",
      password: "",
      roleIds: (item.role_ids || []).map((id) => String(id)),
    });
    setShowForm(true);
  }

  function toggleRole(id) {
    const key = String(id);
    setForm((current) => ({
      ...current,
      roleIds: current.roleIds.includes(key)
        ? current.roleIds.filter((value) => value !== key)
        : [...current.roleIds, key],
    }));
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!canCreate && !form.id) return;
    if (!canEdit && form.id) return;
    if (!canAssignRoles) {
      notify("Your account is not allowed to assign roles.", "error");
      return;
    }
    if (form.roleIds.length === 0) {
      notify("Select at least one role.", "error");
      return;
    }
    if (!form.id && form.password.length < 12) {
      notify("Password must contain at least 12 characters.", "error");
      return;
    }
    if (form.id && form.password && form.password.length < 12) {
      notify("New password must contain at least 12 characters.", "error");
      return;
    }

    setSaving(true);
    try {
      if (form.id) {
        await updateAdminUser(
          {
            id: form.id,
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password.trim(),
            roleIds: form.roleIds,
          },
          csrfToken
        );
        notify("Administrator account updated.");
      } else {
        await createAdminUser(
          {
            name: form.name.trim(),
            email: form.email.trim(),
            password: form.password,
            roleIds: form.roleIds,
          },
          csrfToken
        );
        notify("Administrator account created.");
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM, roleIds: [] });
      await loadUsers(pagination.page || 1);
    } catch (error) {
      notify(error.message || "Unable to save administrator account.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange() {
    if (!confirmUser || !canDisable || confirmUser.is_current_user || confirmUser.is_super_admin) return;
    const nextStatus = confirmUser.status === "active" ? "disabled" : "active";
    setSaving(true);
    try {
      await setAdminUserStatus(confirmUser.id, nextStatus, csrfToken);
      notify(nextStatus === "active" ? "Administrator enabled." : "Administrator disabled.");
      setConfirmUser(null);
      await loadUsers(pagination.page || 1);
    } catch (error) {
      notify(error.message || "Unable to change account status.", "error");
    } finally {
      setSaving(false);
    }
  }

  function submitSearch(event) {
    event.preventDefault();
    loadUsers(1);
  }

  if (!user?.permissions?.includes("admin_users.view")) {
    return (
      <div className="bb-au-empty-state">
        <span className="bb-au-empty-icon">♙</span>
        <h2>Admin Users</h2>
        <p>Your account does not have permission to view administrator accounts.</p>
      </div>
    );
  }

  return (
    <div className="bb-au-module">
      {notice ? <div className={`bb-au-notice ${notice.type}`}>{notice.message}</div> : null}

      <div className="bb-au-hero">
        <div>
          <span className="bb-au-chip">ACCESS CONTROL</span>
          <h2>Admin Users</h2>
          <p>Create team accounts, assign multiple operational roles and control account status.</p>
        </div>
        {canCreate ? (
          <button className="bb-au-primary" type="button" onClick={openCreate}>
            + Add administrator
          </button>
        ) : null}
      </div>

      <div className="bb-au-stats">
        <div className="bb-au-stat-card">
          <span>ACCOUNTS</span>
          <strong>{pagination.total}</strong>
          <small>All administrator accounts</small>
        </div>
        <div className="bb-au-stat-card">
          <span>ACTIVE</span>
          <strong>{statusFilter === "active" ? activeCount : users.filter((item) => item.status === "active").length}</strong>
          <small>Enabled accounts on this page</small>
        </div>
        <div className="bb-au-stat-card">
          <span>PROTECTED</span>
          <strong>{protectedCount}</strong>
          <small>Super Admin account(s)</small>
        </div>
        <div className="bb-au-stat-card">
          <span>ASSIGNABLE ROLES</span>
          <strong>{selectableRoles.length}</strong>
          <small>Super Admin is excluded</small>
        </div>
      </div>

      <div className="bb-au-panel">
        <div className="bb-au-toolbar">
          <form className="bb-au-search" onSubmit={submitSearch}>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
              aria-label="Search administrator accounts"
            />
            <button type="submit">Search</button>
          </form>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          <button className="bb-au-secondary" type="button" onClick={() => loadUsers(pagination.page || 1)} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="bb-au-security-banner">
          <div className="bb-au-lock">✓</div>
          <div>
            <strong>Super Admin is protected</strong>
            <span>The Super Admin role cannot be assigned, edited or disabled from this screen. Team members can receive multiple operational roles.</span>
          </div>
        </div>

        <div className="bb-au-table-wrap">
          {loading ? (
            <div className="bb-au-table-state">Loading administrator accounts…</div>
          ) : users.length === 0 ? (
            <div className="bb-au-table-state">
              <strong>No administrator accounts found.</strong>
              <span>Try another search or create a new team account.</span>
            </div>
          ) : (
            <table className="bb-au-table">
              <thead>
                <tr>
                  <th>Administrator</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th className="bb-au-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="bb-au-person">
                        <div className="bb-au-avatar">{initials(item.name)}</div>
                        <div>
                          <strong>{item.name}</strong>
                          <span>{item.email}{item.is_current_user ? " · You" : ""}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="bb-au-role-list">
                        {(item.roles || []).map((role) => (
                          <span className={`bb-au-role ${role === "Super Admin" ? "protected" : ""}`} key={role}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`bb-au-status ${item.status}`}>{item.status === "active" ? "Active" : "Disabled"}</span>
                    </td>
                    <td className="bb-au-muted-cell">{formatDate(item.last_login_at)}</td>
                    <td className="bb-au-actions">
                      {item.is_super_admin ? (
                        <span className="bb-au-protected-label">Protected</span>
                      ) : (
                        <>
                          {canEdit ? (
                            <button type="button" className="bb-au-action" onClick={() => openEdit(item)}>Edit</button>
                          ) : null}
                          {canDisable && !item.is_current_user ? (
                            <button type="button" className="bb-au-action danger" onClick={() => setConfirmUser(item)}>
                              {item.status === "active" ? "Disable" : "Enable"}
                            </button>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination.pages > 1 ? (
          <div className="bb-au-pagination">
            <button type="button" disabled={pagination.page <= 1 || loading} onClick={() => loadUsers(pagination.page - 1)}>Previous</button>
            <span>Page {pagination.page} of {pagination.pages}</span>
            <button type="button" disabled={pagination.page >= pagination.pages || loading} onClick={() => loadUsers(pagination.page + 1)}>Next</button>
          </div>
        ) : null}
      </div>

      {showForm ? (
        <Modal
          title={form.id ? "Edit administrator" : "Create administrator"}
          description="Assign one or more operational roles. Super Admin is intentionally unavailable."
          onClose={() => !saving && setShowForm(false)}
        >
          <form className="bb-au-form" onSubmit={handleSave}>
            <div className="bb-au-form-grid">
              <label>
                <span>Full name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  autoComplete="name"
                  required
                  disabled={saving}
                  placeholder="Ashok Kumar Khandel"
                />
              </label>
              <label>
                <span>Email address</span>
                <input
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                  type="email"
                  autoComplete="email"
                  required
                  disabled={saving}
                  placeholder="name@example.com"
                />
              </label>
              <label className="bb-au-form-wide">
                <span>{form.id ? "New password (optional)" : "Password"}</span>
                <input
                  value={form.password}
                  onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                  type="password"
                  autoComplete={form.id ? "new-password" : "new-password"}
                  required={!form.id}
                  minLength={12}
                  disabled={saving}
                  placeholder="At least 12 characters"
                />
                <small>{form.id ? "Leave blank to keep the existing password." : "Use a unique password of at least 12 characters."}</small>
              </label>
            </div>

            <fieldset className="bb-au-roles-fieldset">
              <legend>Assign roles</legend>
              <p>Select all roles this team member needs. Multiple roles are supported.</p>
              {rolesLoading ? (
                <div className="bb-au-inline-loading">Loading available roles…</div>
              ) : (
                <div className="bb-au-role-grid">
                  {selectableRoles.map((role) => {
                    const id = roleKey(role);
                    const selected = form.roleIds.includes(id);
                    return (
                      <label className={`bb-au-role-option ${selected ? "selected" : ""}`} key={id}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRole(id)}
                          disabled={saving || !canAssignRoles}
                        />
                        <span className="bb-au-checkbox">{selected ? "✓" : ""}</span>
                        <span>
                          <strong>{role.name}</strong>
                          <small>{roleDescriptions[role.name] || "Role-based access for a specific administration function."}</small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>

            <div className="bb-au-protected-note">
              <strong>Super Admin</strong>
              <span>Full system administration remains restricted to the existing protected account and cannot be assigned here.</span>
            </div>

            {!canAssignRoles ? <div className="bb-au-inline-error">You do not have permission to assign roles.</div> : null}

            <div className="bb-au-modal-actions">
              <button className="bb-au-secondary" type="button" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="bb-au-primary" type="submit" disabled={saving || rolesLoading || !canAssignRoles}>
                {saving ? "Saving…" : form.id ? "Save changes" : "Create account"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmUser ? (
        <Modal
          title={`${confirmUser.status === "active" ? "Disable" : "Enable"} administrator?`}
          description={`${confirmUser.name} · ${confirmUser.email}`}
          onClose={() => !saving && setConfirmUser(null)}
        >
          <div className="bb-au-confirm-body">
            <div className="bb-au-confirm-icon">!</div>
            <p>
              {confirmUser.status === "active"
                ? "This account will no longer be able to sign in. Its roles and audit history will remain intact."
                : "This account will be allowed to sign in again with its existing roles."}
            </p>
          </div>
          <div className="bb-au-modal-actions">
            <button className="bb-au-secondary" type="button" onClick={() => setConfirmUser(null)} disabled={saving}>Cancel</button>
            <button className="bb-au-primary danger-fill" type="button" onClick={handleStatusChange} disabled={saving}>
              {saving ? "Saving…" : confirmUser.status === "active" ? "Disable account" : "Enable account"}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
